import { MessageSquareText, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { demoResultsPath } from "@/lib/apex25AppUrl";
import { formatUntil } from "@/pages/apex25/demo/last-demo";
import { useLatestDemo } from "@/pages/apex25/demo/use-latest-demo";

const dismissedKey = (sessionId: string) => `latest-demo-dismissed:${sessionId}`;

const wasDismissed = (sessionId: string) => {
  try {
    return sessionStorage.getItem(dismissedKey(sessionId)) === "1";
  } catch {
    return false;
  }
};

/**
 * Top of the app while the user's last Apex 25 demo is still kept (2 hours): the way back to its
 * results. Hidden once dismissed for this tab, or once it expires.
 */
export const LatestDemoBanner = () => {
  const latest = useLatestDemo();
  const [dismissed, setDismissed] = useState<string | null>(null);

  if (!latest || dismissed === latest.sessionId || wasDismissed(latest.sessionId)) return null;

  const dismiss = () => {
    setDismissed(latest.sessionId);
    try {
      sessionStorage.setItem(dismissedKey(latest.sessionId), "1");
    } catch {
      // The banner just comes back on the next load.
    }
  };

  return (
    <div
      role="status"
      className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-stone-800 md:mx-8"
    >
      <p className="flex items-start gap-2">
        <MessageSquareText className="mt-0.5 size-4 shrink-0 text-amber-700" aria-hidden="true" />
        <span>
          <strong>Your Apex 25 demo results are ready.</strong> Available until{" "}
          {formatUntil(latest.expiresAt)}, then they&apos;re deleted.
        </span>
      </p>
      <div className="flex items-center gap-2">
        <Link
          to={demoResultsPath(latest.sessionId)}
          className="rounded-md bg-stone-800 px-3 py-1.5 font-semibold text-white hover:bg-stone-700"
        >
          View results
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide this message"
          className="rounded-md p-1.5 text-stone-500 hover:bg-amber-100 hover:text-stone-800"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
